import React from 'react';
import type { GanttTimeLineItem } from '@bringg-frontend/gantt-timeline';
import type RoutesPlannerStore from '../../stores/routes-planner-store';
import ConsecutiveRouteTooltipData from './consecutive-route-tooltip-data/consecutive-route-tooltip-data';
import { SECOND_IN_MS } from '../../utils/bringgTypesTransformers';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import type { RoutesPlannerTranslations } from '../routes-planner/translations';
import { getHoursFormat } from '../../utils/date-utils';
import './consecutive-route-tooltip.scss';
import type { RoutesPlannerRun } from '@bringg-frontend/routes-planner';
import type { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';

export interface ConsecutiveRouteTooltipProps {
	gantItems: GanttTimeLineItem[];
	store: RoutesPlannerStore;
	translations: RoutesPlannerTranslations;
}

const ConsecutiveRouteTooltip = ({ gantItems, store, translations }: ConsecutiveRouteTooltipProps) => {
	const { driverName, vehicleName, routeName } = translations.tooltipTranslations;
	const timezone = store.team.time_zone;
	const hourFormat = store.configuration.hourFormat;

	return (
		<div className="consecutive-route-tooltip">
			{gantItems.length > 1 && (
				<div className="consecutive-route-tooltip-icon">
					<BringgIcon iconName={BringgFontIcons.AltRoute} className="bringg-icon-alt-route" />
				</div>
			)}
			<div className="consecutive-route-tooltip-items">
				{gantItems.map(item => {
					const { customData, rowId } = item;
					const { consecutiveRouteId } = customData || {};
					const {
						consecutiveRoutesStore,
						routesById,
						routeInfoByIndex,
						vehiclesById,
						driversById,
						colorsMap
					} = store;
					const consecutiveRoute =
						consecutiveRoutesStore.routesById.get(consecutiveRouteId) ||
						routeInfoByIndex[consecutiveRouteId] ||
						routesById[consecutiveRouteId];

					const vehicleId =
						(consecutiveRoute as RoutesPlannerRun).vehicle_id ||
						(consecutiveRoute as EtaResponseRouteItem).power_unit_id;
					const driverId = consecutiveRoute.user_id;

					const hasSameDriver = driverId === routesById[rowId].user_id;
					const hasSameVehicle = vehicleId === routesById[rowId].vehicle_id;

					return (
						<ConsecutiveRouteTooltipData
							key={consecutiveRouteId}
							color={colorsMap.get(consecutiveRouteId)}
							routeName={
								(consecutiveRoute as RoutesPlannerRun).title || routesById[consecutiveRouteId].title
							}
							routeTimes={[
								getHoursFormat(consecutiveRoute.depot_etl * SECOND_IN_MS, timezone, hourFormat),
								getHoursFormat(consecutiveRoute.depot_back_eta * SECOND_IN_MS, timezone, hourFormat)
							]}
							hasSameDriver={hasSameDriver}
							hasSameVehicle={hasSameVehicle}
							vehicleName={vehiclesById[vehicleId]?.name}
							driverName={driversById[driverId]?.name}
							translations={{ driverName, vehicleName, routeName }}
						/>
					);
				})}
			</div>
		</div>
	);
};

export default ConsecutiveRouteTooltip;
